import React, { createContext, useCallback, useContext, useEffect } from "react";
import useStateWithLocalStorage from "./hooks/useStateWithLocalStorage";

export const LANGUAGES = ["fi", "sv", "en"] as const;
export type Language = (typeof LANGUAGES)[number];

const fi = {
    appTitle: "HSL-kaupunkipyörät",
    favourites: "Suosikit",
    nearby: "Lähimmät asemat",
    findNearby: "Hae lähimmät asemat",
    locating: "Haetaan sijaintia…",
    bikesOne: "pyörä",
    bikesMany: "pyörää",
    bikesLabel: "{count} pyörää telineissä",
    spaces: "Vapaita paikkoja: {count}",
    returnNotPossible: "Palautus ei mahdollista",
    closed: "Asema ei ole käytössä",
    notFound: "Asemaa {id} ei löytynyt",
    loadError: "Tietoja ei saatu.",
    stationsError: "Asemien haku epäonnistui.",
    retry: "Yritä uudelleen",
    updatedJustNow: "Päivitetty juuri nyt",
    updatedSeconds: "Päivitetty {count} s sitten",
    updatedMinutes: "Päivitetty {count} min sitten",
    refresh: "Päivitä nyt",
    addFavourite: "Lisää {name} suosikiksi",
    removeFavourite: "Poista {name} suosikeista",
    share: "Jaa suosikit",
    linkCopied: "Linkki kopioitu",
    directions: "Kävelyreitti asemalle {name}",
    distance: "{distance} päässä",
    empty: "Hae lähimmät asemat tai lisää asemia suosikeiksi tähdellä.",
    locationDenied:
        "Sijaintilupa on estetty. Salli sijainti selaimen asetuksista tai lisää asemia suosikeiksi.",
    locationUnavailable: "Sijaintia ei saatu selvitettyä.",
    locationTimeout: "Sijainnin haku kesti liian kauan. Yritä uudelleen.",
    locationUnsupported: "Selain ei tue sijainnin hakua.",
    language: "Kieli",
};

type Translations = typeof fi;
export type TranslationKey = keyof Translations;

const sv: Translations = {
    appTitle: "HRT:s stadscyklar",
    favourites: "Favoriter",
    nearby: "Närmaste stationer",
    findNearby: "Hitta närmaste stationer",
    locating: "Söker position…",
    bikesOne: "cykel",
    bikesMany: "cyklar",
    bikesLabel: "{count} cyklar i ställen",
    spaces: "Lediga platser: {count}",
    returnNotPossible: "Retur inte möjlig",
    closed: "Stationen är inte i bruk",
    notFound: "Stationen {id} hittades inte",
    loadError: "Kunde inte hämta uppgifterna.",
    stationsError: "Kunde inte hämta stationerna.",
    retry: "Försök igen",
    updatedJustNow: "Uppdaterad just nu",
    updatedSeconds: "Uppdaterad för {count} s sedan",
    updatedMinutes: "Uppdaterad för {count} min sedan",
    refresh: "Uppdatera nu",
    addFavourite: "Lägg till {name} som favorit",
    removeFavourite: "Ta bort {name} från favoriter",
    share: "Dela favoriter",
    linkCopied: "Länken kopierad",
    directions: "Gångväg till {name}",
    distance: "{distance} bort",
    empty: "Hitta närmaste stationer eller lägg till favoriter med stjärnan.",
    locationDenied:
        "Platsåtkomst är blockerad. Tillåt den i webbläsarens inställningar eller lägg till favoriter.",
    locationUnavailable: "Positionen kunde inte fastställas.",
    locationTimeout: "Positionssökningen tog för lång tid. Försök igen.",
    locationUnsupported: "Webbläsaren stöder inte positionering.",
    language: "Språk",
};

const en: Translations = {
    appTitle: "HSL city bikes",
    favourites: "Favourites",
    nearby: "Nearest stations",
    findNearby: "Find nearest stations",
    locating: "Finding your location…",
    bikesOne: "bike",
    bikesMany: "bikes",
    bikesLabel: "{count} bikes at the station",
    spaces: "Free spaces: {count}",
    returnNotPossible: "Returns not possible",
    closed: "Station not in use",
    notFound: "Station {id} not found",
    loadError: "Couldn't load the data.",
    stationsError: "Couldn't load the stations.",
    retry: "Try again",
    updatedJustNow: "Updated just now",
    updatedSeconds: "Updated {count} s ago",
    updatedMinutes: "Updated {count} min ago",
    refresh: "Refresh now",
    addFavourite: "Add {name} to favourites",
    removeFavourite: "Remove {name} from favourites",
    share: "Share favourites",
    linkCopied: "Link copied",
    directions: "Walking directions to {name}",
    distance: "{distance} away",
    empty: "Find the nearest stations or add favourites with the star.",
    locationDenied:
        "Location access is blocked. Allow it in your browser settings or add favourites.",
    locationUnavailable: "Couldn't determine your location.",
    locationTimeout: "Finding your location took too long. Try again.",
    locationUnsupported: "Your browser doesn't support location.",
    language: "Language",
};

export const translations: Record<Language, Translations> = { fi, sv, en };

export const LOCALES: Record<Language, string> = {
    fi: "fi-FI",
    sv: "sv-FI",
    en: "en-GB",
};

type Params = Record<string, string | number>;

export const translate = (
    language: Language,
    key: TranslationKey,
    params: Params = {}
) =>
    translations[language][key].replace(/\{(\w+)\}/g, (match, name) =>
        name in params ? String(params[name]) : match
    );

export const detectLanguage = (
    languages: ReadonlyArray<string> = navigator.languages ?? [
        navigator.language,
    ]
): Language => {
    for (const tag of languages) {
        const base = tag?.slice(0, 2).toLowerCase();
        if ((LANGUAGES as ReadonlyArray<string>).includes(base)) {
            return base as Language;
        }
    }
    return "fi";
};

type I18nContextValue = {
    language: Language;
    locale: string;
    setLanguage: (language: Language) => void;
    t: (key: TranslationKey, params?: Params) => string;
};

const I18nContext = createContext<I18nContextValue>({
    language: "fi",
    locale: LOCALES.fi,
    setLanguage: () => {},
    t: (key, params) => translate("fi", key, params),
});

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
    const [storedLanguage, setLanguage] =
        useStateWithLocalStorage<Language | null>({
            localStorageKey: "language",
            defaultValue: null,
        });
    const language =
        storedLanguage && LANGUAGES.includes(storedLanguage)
            ? storedLanguage
            : detectLanguage();

    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    const t = useCallback(
        (key: TranslationKey, params?: Params) =>
            translate(language, key, params),
        [language]
    );

    return (
        <I18nContext.Provider
            value={{ language, locale: LOCALES[language], setLanguage, t }}
        >
            {children}
        </I18nContext.Provider>
    );
};

export const useI18n = () => useContext(I18nContext);
